<template>
  <div>
    <v-breadcrumbs
      :items="[{ title: $t('memoru.albumList'), href: '/memoru/top', color:'primary' }, { title: '' }]"
      class="px-0"
    ></v-breadcrumbs>

    <div class="d-flex align-center">
      <div class="pr-2">
        <v-chip v-if="available && album" color="success" variant="flat">
          {{ $t('memoru.published') }}
        </v-chip>
        <v-chip v-else color="error">{{ $t('memoru.notPublished') }}</v-chip>
      </div>
      <h3>{{ album ? album.title : "" }}</h3>
      <v-spacer />
      <v-btn @click="updateDialog = true" color="primary" variant="outlined">
        <v-icon start>mdi-cog</v-icon>
        {{ $t('memoru.settings') }}
      </v-btn>
    </div>

    <v-card class="mt-4" variant="flat" color="grey-lighten-5">
      <v-container>
        <v-row>
          <v-col cols="3">
            <h5>{{ $t('memoru.category') }}</h5>
            <v-select
              v-model="searchFilters.category"
              :items="multimediaCategoryType"
              variant="outlined"
              hide-details
              density="compact"
            ></v-select>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('memoru.minSalesCount') }}</h5>
            <v-text-field
              v-model="searchFilters.purchasedCountMin"
              variant="outlined"
              hide-details
              density="compact"
              :suffix="$t('memoru.pieces')"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('memoru.photoStartDate') }}</h5>
            <v-text-field
              v-model="searchFilters.dateFrom"
              variant="outlined"
              hide-details
              density="compact"
              type="date"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('memoru.photoEndDate') }}</h5>
            <v-text-field
              v-model="searchFilters.dateTo"
              variant="outlined"
              hide-details
              density="compact"
              type="date"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('common.status') }}</h5>
            <v-select
              v-model="searchFilters.status"
              :items="statusOptions"
              item-title="label"
              item-value="value"
              variant="outlined"
              hide-details
              density="compact"
            >
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center">
                  <v-icon 
                    v-if="item.raw.color" 
                    size="small" 
                    :color="item.raw.color" 
                    class="mr-2"
                  >
                    mdi-circle
                  </v-icon>
                  {{ item.title }}
                </div>
              </template>

              <template v-slot:item="{ item, props }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend v-if="item.raw.color">
                    <v-icon size="small" :color="item.raw.color" class="mr-2">
                      mdi-circle
                    </v-icon>
                  </template>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('memoru.serialNumber') }}</h5>
            <v-text-field
              v-model="searchFilters.serialNumber"
              variant="outlined"
              hide-details
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col cols="4">
            <v-btn
              @click="listContents"
              :loading="loading"
              color="primary"
              block
              variant="flat"
              >{{ $t('common.search') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <div class="pt-4">
      <v-container>
        <v-row v-if="contents">
          <v-col>
            <h5>{{ $t('memoru.total') }} {{ contents.total || 0 }} {{ $t('memoru.items') }}</h5>
          </v-col>
        </v-row>
        <v-row v-if="contents">
          <v-col cols="3" v-for="(item, i) in contents.items" :key="i">
            <memoru-content-card
              @click="onClickContent(item)"
              :loading="loading"
              :content="item"
            ></memoru-content-card>
          </v-col>
        </v-row>
      </v-container>

      <v-pagination
        class="pt-4"
        v-if="contents"
        v-model="page"
        :length="Math.ceil(contents.total / searchFilters.limit)"
      ></v-pagination>
    </div>

    <v-dialog v-model="updateDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title> {{ $t('memoru.albumSettings') }} </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="updateAlbumInput.title"
                  :rules="[required]"
                  :label="$t('memoru.title') + '*'"
                  hide-details
                  variant="outlined"
                  density="compact"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="updateAlbumInput.year"
                  :rules="[required]"
                  :label="$t('memoru.schoolYear') + '*'"
                  hide-details
                  :suffix="$t('memoru.schoolYear')"
                  variant="outlined"
                  density="compact"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="12">
                <v-select
                  v-model="updateAlbumInput.nurseryClassIds"
                  :items="classes"
                  item-title="className"
                  item-value="classId"
                  :label="$t('common.class')"
                  hide-details
                  variant="outlined"
                  density="compact"
                  multiple
                  chips
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  type="date"
                  v-model="internalDateAfter"
                  :label="$t('memoru.releaseStartDate')"
                  hide-details
                  clearable
                  variant="outlined"
                  density="compact"
                  @update:model-value="updateCombinedDateTimeAfter"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  type="time"
                  v-model="internalTimeAfter"
                  :label="$t('memoru.releaseStartTime')"
                  hide-details
                  clearable
                  variant="outlined"
                  density="compact"
                  @update:model-value="updateCombinedDateTimeAfter"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-if="showAvailableBefore"
                  type="date"
                  v-model="internalDateBefore"
                  :label="$t('memoru.releaseEndDate')"
                  hide-details
                  clearable
                  variant="outlined"
                  density="compact"
                  @update:model-value="updateCombinedDateTimeBefore"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-if="showAvailableBefore"
                  type="time"
                  v-model="internalTimeBefore"
                  :label="$t('memoru.releaseEndTime')"
                  hide-details
                  clearable
                  variant="outlined"
                  density="compact"
                  @update:model-value="updateCombinedDateTimeBefore"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="updateAlbumInput.comment"
                  :label="$t('memoru.comment')"
                  hide-details
                  variant="outlined"
                  density="compact"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="updateDialog = false" color="grey" variant="text">
                {{ $t('common.cancel') }}
              </v-btn>
              <v-spacer />
              <v-btn
                @click="updateAlbum"
                color="primary"
                :loading="loading"
                variant="flat"
                >{{ $t('common.save') }}
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailDialog" max-width="1200px">
      <v-card>
        <v-card-title>{{ $t('common.detail') }}</v-card-title>
        <v-card-text>
          <v-container v-if="selectedContent">
            <v-row v-if="selectedContent.category == 'photo'" justify="center">
              <v-col cols="8">
                <v-img
                  height="500"
                  :src="selectedContent.downloadImageUrl"
                ></v-img>
              </v-col>
            </v-row>
            <v-row v-if="selectedContent.category == 'movie'" justify="center">
              <v-col cols="8">
                <video
                  width="100%"
                  height="500"
                  muted
                  controls
                  :src="selectedContent.downloadImageUrl"
                ></video>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-col cols="2">
                <v-btn
                  @click="deleteContent"
                  :loading="loading"
                  color="error"
                  variant="flat"
                  size="small"
                >
                  {{ $t('common.delete') }}
                </v-btn>
              </v-col>
              <v-spacer />
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MemoruContentCard from "@/components/atoms/MemoruContentCard.vue";
import { memoruModule } from "@/store/dataModules/memoruModule";
import {
  MultimediaAlbumResponse,
  MultimediaListResponse,
  MultimediaResponse,
  NurseryClassResponse,
} from "chaild-api/lib";
import { multimediaCategoryType } from "@/constants/memoru";
import dayjs from "dayjs";
import ApiMemoru from "@/api/ApiMemoru";
import ValidationMixin from "@/mixins/ValidationMixin";
import { classModule } from "@/store/dataModules/classModule";
import LocalDataService from "@/service/LocalDataService";
import { snackbarModule } from "@/store/viewModules/snackbarModule";

export default defineComponent({
  name: "MemoruContent",

  mixins: [ValidationMixin],

  components: {
    MemoruContentCard,
  },

  async mounted() {
    await this.listContents();
    await this.getAlbum();
    await this.listClasses();
  },

  data() {
    return {
      selectedStatus: "",
      multimediaCategoryType: multimediaCategoryType,
      loading: false,
      updateDialog: false,
      detailDialog: false,
      searchFilters: {
        category: "",
        dateFrom: "",
        dateTo: "",
        purchasedCountMin: "",
        limit: 20,
        skip: 0,
        serialNumber: "",
      },
      updateAlbumInput: {
        title: "",
        year: "" as string | number,
        nurseryClassIds: [] as any[],
        comment: "",
        availableAfter: "",
        availableBefore: "" as string | null,
      },
      selectedContent: null as null | MultimediaResponse,
      showAvailableBefore: true,
      internalDateAfter: '' as string | null,
      internalTimeAfter: '00:00' as string | null,
      internalDateBefore: '' as string | null,
      internalTimeBefore: '00:00' as string | null,
    };
  },

  computed: {
    statusOptions() {
      return [
        { label: this.$t('common.all'), value: '', color: '' },
        { label: this.$t('memoru.processing'), value: 'inProgress', color: '#ff9800' },
        { label: this.$t('memoru.completed'), value: 'completed', color: '#4caf50' },
        { label: this.$t('memoru.failed'), value: 'failed', color: '#FF5252' }
      ];
    },
    albumId(): string {
      const p = this.$route.params.albumId;
      return typeof p === "string" ? p : p?.[0] ?? "";
    },
    contents: function (): MultimediaListResponse | null {
      return memoruModule.contents;
    },
    album: function (): MultimediaAlbumResponse | null {
      return memoruModule.album;
    },
    available: function (): boolean {
      if (this.album) {
        if (
          dayjs().isAfter(this.album.availableAfter) &&
          (!this.album.availableBefore ||
            dayjs().isBefore(this.album.availableBefore))
        ) {
          return true;
        }
        return false;
      }
      return true;
    },
    page: {
      get(): number {
        // ⭕️ 修正2: ページ番号の計算ロジックを修正
        return Math.floor(this.searchFilters.skip / this.searchFilters.limit) + 1;
      },
      set(value: number) {
        this.searchFilters.skip = (value - 1) * this.searchFilters.limit;
        this.listContents();
        // ※ 元コードにあった this.searchFilters.skip = value * this.searchFilters.limit; はバグなので削除
      },
    },
    classes(): NurseryClassResponse[] {
      if (this.updateAlbumInput.year) {
        return classModule.classes.filter((c) => {
          return String(c.year) == this.updateAlbumInput.year;
        });
      } else {
        return classModule.classes;
      }
    },
  },

  watch: {
    internalDateBefore: 'updateCombinedDateTimeBefore',
    internalTimeBefore: 'updateCombinedDateTimeBefore',
    'updateAlbumInput.availableBefore': {
      handler(newValue) {
        if (newValue) {
          const parts = newValue.split('T');
          if (parts.length === 2) {
            this.internalDateBefore = parts[0];
            this.internalTimeBefore = parts[1];
          }
        } else {
          this.internalDateBefore = '';
          this.internalTimeBefore = '00:00';
        }
      },
      immediate: true
    },
    internalDateAfter: 'updateCombinedDateTimeAfter',
    internalTimeAfter: 'updateCombinedDateTimeAfter',
    'updateAlbumInput.availableAfter': {
      handler(newValue) {
        if (newValue) {
          const parts = newValue.split('T');
          if (parts.length === 2) {
            this.internalDateAfter = parts[0];
            this.internalTimeAfter = parts[1];
          }
        } else {
          this.internalDateAfter = '';
          this.internalTimeAfter = '00:00';
        }
      },
      immediate: true
    },
  },

  methods: {
    updateCombinedDateTimeAfter() {
      if (this.internalDateAfter) {
        const combinedDateTimeAfter = `${this.internalDateAfter}T${this.internalTimeAfter || '00:00'}`;
        this.updateAlbumInput.availableAfter = combinedDateTimeAfter;
      } else {
        this.updateAlbumInput.availableAfter = '';
      }
    },
    updateCombinedDateTimeBefore() {
      if (this.internalDateBefore) {
        const combinedDateTimeBefore = `${this.internalDateBefore}T${this.internalTimeBefore || '00:00'}`;
        this.updateAlbumInput.availableBefore = combinedDateTimeBefore;
      } else {
        this.updateAlbumInput.availableBefore = '';
      }
    },
    async listContents() {
      this.loading = true;
      await memoruModule.listContents({
        ...this.searchFilters,
        albumId: this.albumId,
      });
      this.loading = false;
    },
    async getAlbum() {
      this.loading = true;
      if (this.albumId) {
        await memoruModule
          .getAlbum({
            albumId: this.albumId,
          })
          .catch();
        if (this.album) {
          this.updateAlbumInput = {
            title: this.album.title,
            year: this.album.year,
            nurseryClassIds:
              this.album.nurseryClasses.map((c) => c.classId) || [],
            comment: this.album.comment || "",
            availableAfter: this.formatDate(this.album.availableAfter),
            availableBefore: this.formatDate(this.album.availableBefore ?? ''),
          };
          if (!this.updateAlbumInput.availableBefore) {
            this.internalDateBefore = null;
            this.internalTimeBefore = null;
          }

          this.showAvailableBefore = false;
          this.$nextTick(() => {
            this.showAvailableBefore = true;
          })
        }
      }
      this.loading = false;
    },
    async updateAlbum() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiMemoru.updateAlbum({
          nurseryId,
          albumId: this.albumId,
          ...this.updateAlbumInput,
        }).catch(() => (this.loading = false));
        if (response) {
          snackbarModule.snackOn({
            message: this.$t('memoru.albumUpdated'),
          });
          this.updateDialog = false;
          await this.listContents();
          await this.getAlbum();
        }
      }
      this.loading = false;
    },
    async deleteContent() {
      const answer = confirm(this.$t('memoru.confirmDelete'));
      if (answer && this.selectedContent) {
        this.loading = true;
        const response = await ApiMemoru.deleteContent({
          multimediaId: this.selectedContent.multimediaId,
        }).catch(() => (this.loading = false));
        if (response) {
          this.detailDialog = false;
          alert(this.$t('ui.deleteSuccess'));
          await this.listContents();
        }
        this.loading = false;
      }
    },
    async listClasses() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule.listClass({
          nurseryId,
        });
      }
    },
    async onClickContent(value: MultimediaResponse) {
      if (value.multimediaId) {
        this.loading = true;
        const response = await ApiMemoru.getContent({
          multimediaId: value.multimediaId,
        }).catch(() => (this.loading = false));
        this.selectedContent = response;
        this.detailDialog = true;
        this.loading = false;
      }
    },
    formatDate(value: string): string {
      if (!value) {
        return "";
      }
      return dayjs(value).format("YYYY-MM-DDTHH:mm");
    },
  },
});
</script>

<style scoped></style>